import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate(sessionStorage.getItem("after_login") || "/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white relative overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-blue-700/30 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="rounded-lg bg-white px-4 py-3"><img src="/bshpl-logo.png" alt="BSHPL logo" className="h-10 w-auto" data-testid="login-logo" /></div>
          <div className="font-heading font-bold">Balaji Sweet House Private Limited</div>
        </div>
        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">Employee Advance<br />Management System</h1>
          <p className="mt-4 text-slate-300 max-w-md">Track outstanding advances, record deductions in seconds, and keep a permanent, auditable ledger for every employee.</p>
          <div className="mt-8 max-w-xs rounded-xl border border-white/10 bg-gradient-to-br from-white/10 to-white/[0.02] px-6 py-4 text-center backdrop-blur-sm" data-testid="login-rinset-brand">
            <div className="font-heading text-2xl xl:text-3xl font-black tracking-[0.18em] bg-gradient-to-r from-white via-blue-100 to-blue-300 bg-clip-text text-transparent pl-[0.18em]" data-testid="login-rinset-name">RINSET</div>
            <div className="mt-2 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.3em] text-blue-200/80" data-testid="login-rinset-powered">
              <span className="h-px w-5 bg-blue-300/40" />Powered by BSHPL<span className="h-px w-5 bg-blue-300/40" />
            </div>
          </div>
        </div>
        <div className="relative text-xs text-slate-400 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Internal use only · All activity is audited</div>
      </div>
      <div className="flex items-center justify-center p-6 bg-slate-50">
        <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-sm p-7 fade-up" data-testid="login-form">
          <div className="lg:hidden mb-6">
            <img src="/bshpl-logo.png" alt="BSHPL logo" className="h-9 w-auto mb-3" data-testid="login-logo-mobile" />
            <div className="font-heading font-bold text-slate-900">Balaji Sweet House Pvt. Ltd.</div>
            <div className="text-xs text-slate-500">Employee Advance Ledger</div>
          </div>
          <div className="flex items-center gap-2 text-blue-800"><Lock className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">Secure sign in</span></div>
          <h2 className="text-2xl font-bold text-slate-900 mt-2">Welcome back</h2>
          <p className="text-sm text-slate-500 mt-1">Use the account created by your administrator.</p>
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" data-testid="login-password-input" />
            </div>
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full h-11 bg-blue-800 hover:bg-blue-900" data-testid="login-submit-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
